import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function MicrosoftCallback() {
  const navigate = useNavigate();
  const { handleMicrosoftCallback, verify2FA } = useAuth();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);

  useEffect(() => {
    const processCallback = async () => {
      // Get access token from URL hash
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash);
      const accessToken = params.get('access_token');
      const errorParam = params.get('error');
      const errorDescription = params.get('error_description');

      if (errorParam) {
        setError(errorDescription || 'Microsoft login was cancelled or failed');
        setLoading(false);
        return;
      }

      if (!accessToken) {
        setError('No access token received from Microsoft');
        setLoading(false);
        return;
      }

      // Exchange Microsoft token for app token
      const result = await handleMicrosoftCallback(accessToken);

      if (result.requires_2fa) {
        setRequires2FA(true);
        setTempToken(result.temp_token);
        setLoading(false);
        return;
      }

      if (result.success) {
        if (result.is_new_user) {
          toast.success('Welcome! Your account has been created.');
        } else {
          toast.success('Successfully signed in with Microsoft');
        }
        
        // Redirect to app or stored return URL
        const returnUrl = sessionStorage.getItem('ms_auth_return_url') || '/app';
        sessionStorage.removeItem('ms_auth_return_url');
        navigate(returnUrl);
      } else {
        setError(result.error || 'Failed to authenticate with Microsoft');
        setLoading(false);
      }
    };

    processCallback();
  }, [handleMicrosoftCallback, navigate]);

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    if (!twoFactorCode || twoFactorCode.length < 6) {
      toast.error('Please enter a valid code');
      return;
    }
    
    setVerifying2FA(true);
    
    const result = await verify2FA(twoFactorCode, tempToken);
    
    if (result.success) {
      toast.success('Login successful');
      const returnUrl = sessionStorage.getItem('ms_auth_return_url') || '/app';
      sessionStorage.removeItem('ms_auth_return_url');
      navigate(returnUrl);
    } else {
      toast.error(result.error || 'Invalid 2FA code');
    }
    
    setVerifying2FA(false);
  };

  // 2FA Required View
  if (requires2FA) {
    return (
      <div className="min-h-screen bg-[#F9F7F5] flex items-center justify-center px-4" data-testid="ms-2fa-page">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-6 justify-center">
            <div className="w-12 h-12 bg-[#6B8E6B]/10 rounded-full flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#6B8E6B]" />
            </div>
          </div>
          
          <h1 className="font-heading text-2xl text-[#2C2420] text-center mb-2">
            Two-Factor Authentication
          </h1>
          <p className="text-[#5D524D] text-center mb-6">
            Enter the code from your authenticator app
          </p>

          <form onSubmit={handle2FASubmit} className="space-y-4">
            <Input
              type="text"
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value.replace(/[^A-Za-z0-9-]/g, ''))}
              placeholder="000000"
              className="caffeine-input text-center text-2xl tracking-widest"
              maxLength={9}
              autoComplete="one-time-code"
              autoFocus
              data-testid="ms-2fa-code-input"
            />

            <Button 
              type="submit" 
              className="caffeine-btn-primary w-full"
              disabled={verifying2FA}
              data-testid="ms-verify-2fa-btn"
            >
              {verifying2FA ? 'Verifying...' : 'Verify & Sign In'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Error View
  if (error) {
    return (
      <div className="min-h-screen bg-[#F9F7F5] flex items-center justify-center px-4" data-testid="ms-callback-error">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="font-heading text-2xl text-[#2C2420] mb-2">Authentication Failed</h1>
          <p className="text-[#5D524D] mb-6">{error}</p>
          <Button 
            onClick={() => navigate('/login')}
            className="caffeine-btn-primary"
          >
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  // Loading View
  return (
    <div className="min-h-screen bg-[#F9F7F5] flex items-center justify-center" data-testid="ms-callback-loading">
      <div className="text-center">
        <div className="w-12 h-12 border-3 border-[#4A3B32] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#5D524D]">Signing in with Microsoft...</p>
      </div>
    </div>
  );
}
